import type { IUsersApiUserInList } from '../../../../redux/api/users/users-api.types';
import { IUser } from '../../../../types/interfaces/user';
import type {
  IUserTrainingAccessPostBody,
  IUserTrainingAccessSectionKey,
  IUserTrainingAccessUserRow,
} from '../../../../types/interfaces/user-training-access';

export function sortUsersByLastThenFirstName(a: IUser, b: IUser): number {
  const la = a.lastname?.toLowerCase() ?? '';
  const lb = b.lastname?.toLowerCase() ?? '';
  if (la !== lb) {
    return la.localeCompare(lb);
  }
  const fa = a.firstname?.toLowerCase() ?? '';
  const fb = b.firstname?.toLowerCase() ?? '';
  return fa.localeCompare(fb);
}

export function accessRowToIUser(row: IUserTrainingAccessUserRow): IUser {
  return {
    id: row.id,
    firstname: row.firstname,
    lastname: row.lastname,
    avatar: row.avatar,
  };
}

export function listUserToIUser(u: IUsersApiUserInList): IUser {
  return {
    id: u.id,
    firstname: u.firstname,
    lastname: u.lastname,
    avatar: u.avatar,
  };
}

export function mergeSubscribersWithAccessRows(
  subscribers: IUsersApiUserInList[],
  accessRows: IUserTrainingAccessUserRow[],
): IUser[] {
  const map = new Map<string, IUser>();
  subscribers.forEach((s) => {
    map.set(s.id, listUserToIUser(s));
  });
  accessRows.forEach((row) => {
    if (!map.has(row.id)) {
      map.set(row.id, accessRowToIUser(row));
    }
  });
  return [...map.values()].sort(sortUsersByLastThenFirstName);
}

export function rowsToSortedIUsers(
  rows: IUserTrainingAccessUserRow[],
): IUser[] {
  return rows.map(accessRowToIUser).sort(sortUsersByLastThenFirstName);
}

export function computeIdDelta(
  previousIds: string[],
  nextIds: string[],
): { add: string[]; remove: string[] } {
  const prev = new Set(previousIds);
  const next = new Set(nextIds);
  const add = [...next].filter((id) => !prev.has(id));
  const remove = [...prev].filter((id) => !next.has(id));
  return { add, remove };
}

export function buildPostBodyForSection(
  section: IUserTrainingAccessSectionKey,
  add: string[],
  remove: string[],
): IUserTrainingAccessPostBody {
  const delta: { add?: string[]; remove?: string[] } = {};
  if (add.length > 0) {
    delta.add = add;
  }
  if (remove.length > 0) {
    delta.remove = remove;
  }
  if (Object.keys(delta).length === 0) {
    return {};
  }
  return { [section]: delta } as IUserTrainingAccessPostBody;
}
